import React, { FC, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import { AppIntegrationDetails, IntegrationDetails } from "models";

import { INTEGRATION_CATALOG } from "../integrationCatalog";
import { failureText, IntegrationHealthInfo, rowStatus, STATUS_META } from "../integrationStatus";

import style from "./style.scss";

/** One lead's delivery, from App.integrationHealth.recentDeliveries. */
export interface IntegrationDelivery {
    leadId: string;
    leadDate: string;
    leadName?: string | null;
    outcome: string;
    reason: string;
    statusCode?: number | null;
    errorMessage?: string | null;
}

/** An integration's delivery health with its recent deliveries (App.integrationHealth). */
export interface IntegrationDeliveryHealth extends IntegrationHealthInfo {
    integrationId: string;
    rawIntegrationId: string;
    recentDeliveries: IntegrationDelivery[];
}

export interface ResendSummary {
    sent: number;
    failed: number;
    lastError?: string;
}

export interface IntegrationDetailsDialogProps {
    /** The integration to show; nothing is shown without one */
    integrationId?: string;
    data?: AppIntegrationDetails;
    /** Its delivery health; absent when nothing was sent to it in the window */
    health?: IntegrationDeliveryHealth;
    organizationId?: string;
    appId?: string;
    /** Sends these leads to the integration again, one at a time */
    onResend?(rawIntegrationId: string, deliveries: IntegrationDelivery[]): Promise<ResendSummary>;
    onDisconnect?(integrationId: string): void;
    onClose(): void;
}

const onOff = (value?: boolean | null) => (value ? "On" : "Off");

/**
 * What we know about a connected integration. Only what the integrationStatus query already
 * returns, never the stored token: `integration(type:)` can return raw credentials, and those must
 * not reach the screen.
 */
export function integrationDetails(integrationId: string, data: AppIntegrationDetails = {}): [string, string][] {
    if (integrationId === "searchlight") {
        const feed = data.searchLightFeed;
        return [
            ["Status", feed?.enabled ? "Feed on" : "Feed off"],
            ...(feed?.accountId ? ([["SearchLight account", feed.accountId]] as [string, string][]) : []),
        ];
    }

    const integration = (data as Record<string, IntegrationDetails | undefined>)[integrationId];
    const rows: [string, string][] = [
        ["Status", integration?.isActive ? "Connected and working" : "Needs attention: reconnect it"],
    ];

    switch (integrationId) {
        case "servicetitan": {
            const st = data.servicetitan;
            rows.push(["Lead import", onOff(st?.leadImport)]);
            rows.push(["Job outcome tracking", onOff(st?.outcomeTracking)]);
            rows.push([
                "Invoice access",
                st?.invoicesRead === true
                    ? "Granted"
                    : st?.invoicesRead === false
                    ? "Not granted: upgrade the XAPP app in ServiceTitan"
                    : "Not checked yet",
            ]);
            break;
        }
        case "serviceminder":
            rows.push(["Live availability", onOff(data.serviceminder?.liveAvailability)]);
            break;
        case "webhook":
            rows.push(["Payload", data.webhook?.flattenPayload ? "Flat (single level)" : "Nested"]);
            break;
        case "free2grow":
            if (data.free2grow?.referenceId) {
                rows.push(["Reference ID", data.free2grow.referenceId]);
            }
            break;
    }
    return rows;
}

const OUTCOME: Record<string, { label: string; tone: "good" | "warn" | "idle" }> = {
    SUCCESS: { label: "Sent", tone: "good" },
    MISSING_REF: { label: "Sent", tone: "good" },
    IN_PROGRESS: { label: "Sending", tone: "idle" },
    FAILED: { label: "Didn't go through", tone: "warn" },
};

const when = (iso: string) =>
    new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

export const IntegrationDetailsDialog: FC<IntegrationDetailsDialogProps> = ({
    integrationId,
    data,
    health,
    organizationId,
    appId,
    onResend,
    onDisconnect,
    onClose,
}) => {
    const catalog = integrationId ? INTEGRATION_CATALOG[integrationId] : undefined;
    const name = catalog?.name || integrationId || "";
    const isActive = integrationId
        ? (data as Record<string, { isActive?: boolean | null } | undefined>)?.[integrationId]?.isActive
        : undefined;
    const status = rowStatus(isActive, health);
    const meta = STATUS_META[status];
    const deliveries = health?.recentDeliveries || [];
    // Oldest first, so a resend replays them in the order they came in
    const failedDeliveries = deliveries.filter((d) => d.outcome === "FAILED").reverse();

    const [confirmDisconnect, setConfirmDisconnect] = useState(false);
    const [resending, setResending] = useState(false);
    const [resendResult, setResendResult] = useState<ResendSummary>();
    useEffect(() => {
        setConfirmDisconnect(false);
        setResendResult(undefined);
    }, [integrationId]);

    const resend = async () => {
        if (!onResend || !health) {
            return;
        }
        setResending(true);
        try {
            setResendResult(await onResend(health.rawIntegrationId, failedDeliveries));
        } catch (error) {
            setResendResult({
                sent: 0,
                failed: failedDeliveries.length,
                lastError: error instanceof Error ? error.message : undefined,
            });
        } finally {
            setResending(false);
        }
    };

    return (
        <Dialog
            open={!!integrationId}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            aria-labelledby="integration-details-title"
        >
            {integrationId && (
                <>
                    <DialogTitle id="integration-details-title" component="h2">
                        {name}
                        <span className={`${style.statusPill} ${style[`tone_${meta.tone}`]}`} data-health-status>
                            <span className={style.filterDot + " " + style[`tone_${meta.tone}`]} aria-hidden="true" />
                            {meta.label}
                        </span>
                    </DialogTitle>
                    <DialogContent>
                        {status === "NOT_CONNECTED" ? (
                            <div className={`${style.healthBanner} ${style.tone_bad}`} data-health-banner>
                                <strong>{name} isn&apos;t accepting our connection.</strong>
                                <p>
                                    New leads aren&apos;t reaching it. Reconnect it from its row to start sending leads
                                    again.
                                </p>
                            </div>
                        ) : (status === "FAILING" || status === "NEEDS_ATTENTION") && health ? (
                            <div className={`${style.healthBanner} ${style[`tone_${meta.tone}`]}`} data-health-banner>
                                <strong>
                                    {plural(health.failed, "lead")} didn&apos;t reach {name}
                                </strong>
                                <p>
                                    {failureText(name, health.lastFailureReason, health.lastErrorMessage)}. The leads
                                    are safe in XAPP and can be sent again once it&apos;s fixed.
                                </p>
                            </div>
                        ) : null}

                        {health ? (
                            <>
                                <div className={style.healthCounts}>
                                    <div data-health-count>
                                        <strong>{health.succeeded}</strong>
                                        <span>sent in the last 7 days</span>
                                    </div>
                                    <div data-health-count>
                                        <strong>{health.failed}</strong>
                                        <span>didn&apos;t go through</span>
                                    </div>
                                    <div data-health-count>
                                        <strong>
                                            {health.lastSuccessAt
                                                ? `${formatDistanceToNowStrict(new Date(health.lastSuccessAt))} ago`
                                                : "—"}
                                        </strong>
                                        <span>last one that worked</span>
                                    </div>
                                </div>

                                {deliveries.length > 0 && (
                                    <>
                                        <h3 className={style.detailsHeading}>Recent leads</h3>
                                        <ul className={style.deliveryList}>
                                            {deliveries.slice(0, 10).map((d) => {
                                                const outcome = OUTCOME[d.outcome] || OUTCOME.FAILED;
                                                return (
                                                    <li
                                                        key={`${d.leadId}-${d.leadDate}`}
                                                        className={style[`tone_${outcome.tone}`]}
                                                        data-delivery
                                                    >
                                                        {organizationId && appId ? (
                                                            <RouterLink
                                                                to={`/${organizationId}/${appId}/leads/${d.leadId}`}
                                                            >
                                                                {d.leadName || "Lead"}
                                                            </RouterLink>
                                                        ) : (
                                                            <span>{d.leadName || "Lead"}</span>
                                                        )}
                                                        <span className={style.deliveryDate}>{when(d.leadDate)}</span>
                                                        <span className={style.deliveryOutcome}>
                                                            <span
                                                                className={`${style.filterDot} ${
                                                                    style[`tone_${outcome.tone}`]
                                                                }`}
                                                                aria-hidden="true"
                                                            />
                                                            {outcome.label}
                                                            {d.outcome === "FAILED" && d.statusCode
                                                                ? ` · ${d.statusCode}`
                                                                : ""}
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </>
                                )}
                            </>
                        ) : (
                            status !== "NOT_CONNECTED" && (
                                <p className={style.detailsSummary}>No leads were sent to {name} in the last 7 days.</p>
                            )
                        )}

                        <h3 className={style.detailsHeading}>How it&apos;s set up</h3>
                        {catalog && <p className={style.detailsSummary}>{catalog.summary}</p>}
                        <dl className={style.detailsList}>
                            {integrationDetails(integrationId, data).map(([label, value]) => (
                                <div key={label} data-detail>
                                    <dt>{label}</dt>
                                    <dd>{value}</dd>
                                </div>
                            ))}
                        </dl>

                        {resendResult && (
                            <p className={style.detailsSummary} role="status" data-resend-result>
                                {resendResult.failed === 0
                                    ? `${resendResult.sent} sent.`
                                    : `${resendResult.sent} sent, ${resendResult.failed} still didn't go through${
                                          resendResult.lastError ? `: ${resendResult.lastError}` : ""
                                      }.`}
                            </p>
                        )}

                        {confirmDisconnect && (
                            <div className={`${style.healthBanner} ${style.tone_bad}`} data-confirm-disconnect>
                                <strong>Disconnect {name}?</strong>
                                <p>New leads stop going to {name}. Leads already sent stay there.</p>
                                <div className={style.rowButtons} style={{ justifyContent: "flex-start" }}>
                                    <button
                                        type="button"
                                        className={style.rowButtonPrimary}
                                        onClick={() => onDisconnect?.(integrationId)}
                                    >
                                        Yes, disconnect
                                    </button>
                                    <button
                                        type="button"
                                        className={style.rowButton}
                                        onClick={() => setConfirmDisconnect(false)}
                                    >
                                        Keep it
                                    </button>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                    <DialogActions>
                        {onResend && status !== "NOT_CONNECTED" && failedDeliveries.length > 0 && (
                            <button
                                type="button"
                                className={style.rowButtonPrimary}
                                onClick={resend}
                                disabled={resending}
                            >
                                {resending
                                    ? "Sending…"
                                    : // The list holds only the newest deliveries; the week can have more failures than it does
                                      `Send the ${
                                          (health?.failed || 0) > failedDeliveries.length
                                              ? `${failedDeliveries.length} most recent ${
                                                    failedDeliveries.length === 1 ? "failed lead" : "failed leads"
                                                }`
                                              : plural(failedDeliveries.length, "failed lead")
                                      } again`}
                            </button>
                        )}
                        {onDisconnect && !confirmDisconnect && (
                            <button
                                type="button"
                                className={style.dangerLink}
                                onClick={() => setConfirmDisconnect(true)}
                            >
                                Disconnect
                            </button>
                        )}
                        <span style={{ flex: "1 1 auto" }} />
                        <button type="button" className={style.detailsButton} onClick={onClose}>
                            Close
                        </button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};
